import { useCallback, useEffect, useRef, useState } from 'react';
import {
  clearOfflineSession,
  loadOfflineSession,
  saveOfflineSession,
} from '../services/sessionOfflineStore';
import {
  flushOfflineCommandWithBudget,
  isAlreadyScoredConflict,
  rebaseHeadCommand,
} from '../services/sessionOfflineFlush';
import {
  OFFLINE_FLUSH_INTERVAL_MS,
  OFFLINE_FLUSH_ONLINE_DELAY_MS,
  createOfflineCommandId,
} from '../utils/session/offlineQueueConstants';

function emptyRecord(sessionId) {
  return {
    sessionId,
    snapshot: null,
    commands: [],
    blockedReason: null,
    blockedConflict: null,
    offlineMode: false,
    updatedAt: Date.now(),
  };
}

/**
 * Host-only IndexedDB command log for competitive Offline mode.
 */
export default function useSessionOfflineQueue({
  sessionId,
  enabled = true,
  tabInstanceId = null,
  clubId = null,
}) {
  const [record, setRecord] = useState(() => emptyRecord(sessionId));
  const [hydrated, setHydrated] = useState(false);
  const [isFlushing, setIsFlushing] = useState(false);
  const recordRef = useRef(record);
  const actionGenRef = useRef(0);
  const localDecisionGenRef = useRef(0);
  const flushLockRef = useRef(false);
  const autoFlushPausedRef = useRef(false);

  useEffect(() => {
    recordRef.current = record;
  }, [record]);

  useEffect(() => {
    let cancelled = false;
    setHydrated(false);
    if (!sessionId || !enabled) {
      setRecord(emptyRecord(sessionId));
      setHydrated(true);
      return undefined;
    }
    loadOfflineSession(sessionId)
      .then((loaded) => {
        if (!cancelled) {
          const next = loaded.offlineMode && loaded.blockedReason
            ? { ...loaded, blockedReason: null, blockedConflict: null }
            : loaded;
          setRecord(next);
          recordRef.current = next;
          setHydrated(true);
          if (next !== loaded) {
            saveOfflineSession(next).catch((error) => {
              console.error('[offline-queue] Failed to clear leftover pause state:', error);
            });
          }
        }
      })
      .catch((error) => {
        console.error('[offline-queue] Failed to load IndexedDB record:', error);
        if (!cancelled) {
          setRecord(emptyRecord(sessionId));
          setHydrated(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, enabled]);

  const persist = useCallback(async (next) => {
    recordRef.current = next;
    setRecord(next);
    try {
      await saveOfflineSession(next);
    } catch (error) {
      console.error('[offline-queue] Failed to persist IndexedDB record:', error);
    }
  }, []);

  const beginAction = useCallback(() => {
    actionGenRef.current += 1;
    return actionGenRef.current;
  }, []);

  const markLocalDecision = useCallback((token) => {
    if (token) localDecisionGenRef.current = token;
  }, []);

  const shouldIgnoreLateResponse = useCallback((token) => {
    return Boolean(token) && localDecisionGenRef.current >= token;
  }, []);

  const saveSnapshot = useCallback(async (snapshot) => {
    if (!sessionId || !enabled) return;
    const current = recordRef.current || emptyRecord(sessionId);
    await persist({
      ...current,
      sessionId,
      snapshot,
      updatedAt: Date.now(),
    });
  }, [enabled, persist, sessionId]);

  const setOfflineMode = useCallback(async (enabledMode) => {
    if (!sessionId || !enabled) return;
    const current = recordRef.current || emptyRecord(sessionId);
    await persist({
      ...current,
      sessionId,
      offlineMode: Boolean(enabledMode),
      updatedAt: Date.now(),
    });
  }, [enabled, persist, sessionId]);

  const enqueue = useCallback(async (command, snapshot = null) => {
    if (!sessionId || !enabled || !command) return null;
    const current = recordRef.current || emptyRecord(sessionId);
    const nextCommand = {
      id: command.id || createOfflineCommandId(),
      createdAt: Date.now(),
      ...command,
    };
    const next = {
      ...current,
      sessionId,
      snapshot: snapshot ?? current.snapshot,
      commands: [...(current.commands || []), nextCommand],
      blockedReason: null,
      blockedConflict: null,
      updatedAt: Date.now(),
    };
    await persist(next);
    return nextCommand;
  }, [enabled, persist, sessionId]);

  const setBlockedReason = useCallback(async (blockedReason, blockedConflict = null) => {
    const current = recordRef.current || emptyRecord(sessionId);
    await persist({
      ...current,
      blockedReason,
      blockedConflict: blockedReason ? (blockedConflict ?? current.blockedConflict) : null,
      updatedAt: Date.now(),
    });
  }, [persist, sessionId]);

  const discardQueue = useCallback(async () => {
    if (!sessionId) return;
    await persist({
      sessionId,
      snapshot: null,
      commands: [],
      blockedReason: null,
      blockedConflict: null,
      offlineMode: false,
      updatedAt: Date.now(),
    });
    try {
      await clearOfflineSession(sessionId);
    } catch (error) {
      console.error('[offline-queue] Failed to discard IndexedDB record:', error);
    }
  }, [persist, sessionId]);

  const discardQueuedCommands = useCallback(async () => {
    if (!sessionId) return;
    const current = recordRef.current || emptyRecord(sessionId);
    await persist({
      ...current,
      sessionId,
      commands: [],
      blockedReason: null,
      blockedConflict: null,
      snapshot: current.offlineMode ? current.snapshot : null,
      offlineMode: current.offlineMode === true,
      updatedAt: Date.now(),
    });
  }, [persist, sessionId]);

  const persistRemainingCommands = useCallback(async (remaining, extras = {}) => {
    const current = recordRef.current || emptyRecord(sessionId);
    const next = {
      ...current,
      commands: remaining,
      blockedReason: remaining.length === 0 ? null : (extras.blockedReason ?? current.blockedReason),
      blockedConflict: remaining.length === 0 ? null : (extras.blockedConflict ?? current.blockedConflict),
      snapshot: remaining.length === 0 && !current.offlineMode ? null : current.snapshot,
      offlineMode: current.offlineMode === true,
      updatedAt: Date.now(),
    };
    await persist(next);
    if (remaining.length === 0 && sessionId && !current.offlineMode) {
      try {
        await clearOfflineSession(sessionId);
      } catch (error) {
        console.error('[offline-queue] Failed to clear synced record:', error);
      }
    }
  }, [persist, sessionId]);

  const removeHeadCommand = useCallback(async () => {
    const current = recordRef.current || emptyRecord(sessionId);
    await persistRemainingCommands((current.commands || []).slice(1));
  }, [persistRemainingCommands, sessionId]);

  const acknowledgeCommandId = useCallback(async (commandId) => {
    if (!commandId) return 0;
    const current = recordRef.current || emptyRecord(sessionId);
    const remaining = (current.commands || []).filter((command) => command.id !== commandId);
    const removed = (current.commands || []).length - remaining.length;
    if (removed > 0) {
      await persistRemainingCommands(remaining, { blockedReason: null, blockedConflict: null });
    }
    return removed;
  }, [persistRemainingCommands, sessionId]);

  const acknowledgeScoreEvent = useCallback(async (scoreEventId) => {
    if (!scoreEventId) return 0;
    const current = recordRef.current || emptyRecord(sessionId);
    const remaining = (current.commands || []).filter((command) => (
      command.scoreEventId !== scoreEventId && command.causedByScoreEventId !== scoreEventId
    ));
    const removed = (current.commands || []).length - remaining.length;
    if (removed > 0) {
      await persistRemainingCommands(remaining, { blockedReason: null, blockedConflict: null });
    }
    return removed;
  }, [persistRemainingCommands, sessionId]);

  const hasUnsyncedCommands = useCallback(
    () => (recordRef.current?.commands?.length || 0) > 0,
    []
  );
  const hasPending = (record?.commands?.length || 0) > 0;
  const pendingCount = record?.commands?.length || 0;

  const tryFlush = useCallback(async () => {
    if (!enabled || !sessionId) return { flushed: 0 };
    if (flushLockRef.current) return { flushed: 0, busy: true, completed: false };
    let current = recordRef.current || emptyRecord(sessionId);
    if (current.offlineMode && current.blockedReason) {
      current = {
        ...current,
        blockedReason: null,
        blockedConflict: null,
        updatedAt: Date.now(),
      };
      await persist(current);
    }
    if (current.blockedReason || !current.commands?.length) {
      return {
        flushed: 0,
        completed: !current.blockedReason && !(current.commands?.length),
        blockedReason: current.blockedReason || null,
      };
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return { flushed: 0, offline: true, completed: false };
    }

    flushLockRef.current = true;
    setIsFlushing(true);
    let flushed = 0;
    try {
      while ((recordRef.current?.commands || []).length > 0 && !recordRef.current?.blockedReason) {
        const head = recordRef.current.commands[0];
        const result = await flushOfflineCommandWithBudget(head, { sessionId, tabInstanceId, clubId });
        if (result.ok) {
          await removeHeadCommand();
          flushed += 1;
          continue;
        }
        if (result.blocked) {
          if (recordRef.current?.offlineMode) {
            if (isAlreadyScoredConflict(result.result) || result.result?.code === 'MATCH_NOT_ACTIVE') {
              await removeHeadCommand();
              flushed += 1;
              continue;
            }
            const rebased = rebaseHeadCommand(head, result.result || {});
            await persist({
              ...recordRef.current,
              commands: [rebased || head, ...(recordRef.current.commands || []).slice(1)],
              blockedReason: null,
              blockedConflict: null,
              updatedAt: Date.now(),
            });
            return {
              flushed,
              completed: false,
              retry: true,
              message: result.message,
            };
          }
          return {
            flushed,
            completed: false,
            retry: true,
            message: result.message,
          };
        }
        return {
          flushed,
          completed: false,
          retry: true,
          timedOut: result.timedOut === true,
          message: result.message,
        };
      }
      const remaining = recordRef.current?.commands?.length || 0;
      return {
        flushed,
        completed: remaining === 0 && !recordRef.current?.blockedReason,
      };
    } finally {
      flushLockRef.current = false;
      setIsFlushing(false);
    }
  }, [clubId, enabled, persist, removeHeadCommand, sessionId, tabInstanceId]);

  const rebaseHeadAndFlush = useCallback(async () => {
    if (!sessionId) return { flushed: 0 };
    autoFlushPausedRef.current = true;
    try {
      while (flushLockRef.current) {
        await new Promise((resolve) => setTimeout(resolve, 40));
      }
      const current = recordRef.current || emptyRecord(sessionId);
      const head = (current.commands || [])[0];
      if (!head) {
        return { flushed: 0, empty: true };
      }
      const rebased = rebaseHeadCommand(head, current.blockedConflict || {});
      await persist({
        ...current,
        commands: [rebased, ...(current.commands || []).slice(1)],
        blockedReason: null,
        blockedConflict: current.blockedConflict,
        updatedAt: Date.now(),
      });
      return tryFlush();
    } finally {
      autoFlushPausedRef.current = false;
    }
  }, [persist, sessionId, tryFlush]);

  useEffect(() => {
    if (
      !enabled
      || !sessionId
      || !hasPending
      || autoFlushPausedRef.current
      || (record.blockedReason && !record.offlineMode)
    ) {
      return undefined;
    }
    let onlineTimer = null;
    const onOnline = () => {
      if (onlineTimer != null) clearTimeout(onlineTimer);
      onlineTimer = setTimeout(() => {
        onlineTimer = null;
        if (!autoFlushPausedRef.current) void tryFlush();
      }, OFFLINE_FLUSH_ONLINE_DELAY_MS);
    };
    window.addEventListener('online', onOnline);
    const timer = setInterval(() => {
      void tryFlush();
    }, OFFLINE_FLUSH_INTERVAL_MS);
    void tryFlush();
    return () => {
      window.removeEventListener('online', onOnline);
      if (onlineTimer != null) clearTimeout(onlineTimer);
      clearInterval(timer);
    };
  }, [enabled, hasPending, record.blockedReason, sessionId, tryFlush]);

  return {
    hydrated,
    record,
    hasPending,
    hasUnsyncedCommands,
    pendingCount,
    blockedReason: record.blockedReason,
    blockedConflict: record.blockedConflict,
    offlineMode: record.offlineMode === true,
    isFlushing,
    beginAction,
    markLocalDecision,
    shouldIgnoreLateResponse,
    enqueue,
    setOfflineMode,
    saveSnapshot,
    tryFlush,
    setBlockedReason,
    discardQueue,
    discardQueuedCommands,
    rebaseHeadAndFlush,
    acknowledgeCommandId,
    acknowledgeScoreEvent,
  };
}
